import isApiError from "@insite/client-framework/Common/isApiError";
import { ApiParameter, patch, get, ServiceResult, post } from "@insite/client-framework/Services/ApiService";
import { BaseModel } from "@insite/client-framework/Types/ApiModels";
// Below import is a P21 Change
import { EPAValidationRequest } from "@NA/Components/SaveCard/ADINewCreditCardModalPopup";

export interface FiservTokenization {
    Version: string;
    SessionId: string;
    CardNumber: string;
    Expiration: string;
    VerifyCard: boolean;
}

export interface FiservTokenizationResult {
    ErrorMessage: string;
    ErrorCode: string;
}

export interface GetSavedCreditCardsApiParameter extends ApiParameter {
    page?: number;
    pageSize?: number;
    source?: string;
    contains?: string;
}

export interface CreditCardDtoCollection {
    uri: any;
    cardType: string;
    cardNumber: string;
    cardToken: string;
    expiryMonth: string;
    expiryYear: string;
    isDefault: boolean;
    cardCVC: any;
    postalCode: any;
    id: string;
    properties: any;
    // Below added properties are P21 Changes
    creditCardBankNumber: string,
    paymentMethodId: string,
    paymentTypeDesc: string,
    paymentTypeId: string,
    period: string,
    yearForPeriod: string,
}

export interface ADICreditCardsModelNA extends BaseModel {
    ccID: string;
    ccType: string;
    ccNumber: string;
    ccExpiryMonth: string;
    ccExpiryYear: string;
    ccToken: string;
    ccCvc: string;
    ccPostalCode: string;
    xiResponse: string;
    isDefault: boolean;
    properties: { [key: string]: string };
    uri: string;
}

export interface GetSavedCreditCardsModel extends BaseModel {
    uri: string;
    pagination: any;
    customCreditCardPaging: any;
    creditCardDtoCollection: CreditCardDtoCollection[];
    properties: any;
    // Below added property is a P21 Change ---- P21 Change
    paymentTypes: any;
}

export interface GetAccessTokenApiParameter {
    accessToken: string;
    source: string;
}

export interface SaveCreditCardApiParameter {
    creditcard: ADICreditCardsModelNA;
}

export interface DeleteCreditCardApiParameter {
    cardToken: string;
    // Commented below code because of  P21 Change
   // expiryMonth: string;
   // expiryYear: string;
}

type UpdateSavedCreditCardsApiParameter = CreditCardDtoCollection & ApiParameter;

type UpdateSavedCreditCardsModel = CreditCardDtoCollection & BaseModel;

const url = "/api/v2/savedcreditcards";
const accessTokenUrl = "/api/v2/paymenttoken";
const genrateMailUrl = "/api/v2/creditcardpopupdetails";
const validateUrl = "/api/v3/epa/validate";
const deleteUrl = "/api/v2/savedcreditcards/delete";
export async function getSavedCreditCards(
    parameter: GetSavedCreditCardsApiParameter,
): Promise<GetSavedCreditCardsModel> {
    try {
        const savedCreditCards = await get<GetSavedCreditCardsModel>(
            `${url}?${new Date()}&contains=${parameter?.contains}&page=${
                parameter.page ? parameter.page : 1
            }&pageSize=${parameter.pageSize ? parameter.pageSize : 20}&properties={}`,
            // }&properties={"source":"invoices"}`,
            // parameter,
        );
        return savedCreditCards;
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            return error.errorJson.message;
        }
        throw error;
    }
}

export async function updateSavedCreditCards(
    parameter: UpdateSavedCreditCardsApiParameter,
): Promise<ServiceResult<UpdateSavedCreditCardsModel>> {
    try {
        const savedCreditCards = await patch<UpdateSavedCreditCardsModel>(`${url}`, parameter);
        return {
            result: savedCreditCards,
            successful: true,
        };
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        throw error;
    }
}

export async function saveCreditCards(parameter: SaveCreditCardApiParameter): Promise<any> {
    let accessToken = "";
    const creditcard = { ...parameter.creditcard };
    const source =
        creditcard.properties != null && creditcard.properties["source"] != null ? creditcard.properties["source"] : "";

    try {
        const result = await getAccessToken({ accessToken: "getAccessToken", source: source });
        accessToken = result.accessToken;
        creditcard.properties = result.properties;
        creditcard.ccToken = accessToken;
        creditcard.properties["source"] = source;

        if (result.properties["provider"] != null && result.properties["provider"] == "Fiserv") {
            let verifyCard =
                creditcard.properties["verifyCard"] != null &&
                creditcard.properties["verifyCard"].toLowerCase() == "true"
                    ? true
                    : false;
            let ft: FiservTokenization = {
                Version: "1",
                SessionId: accessToken,
                CardNumber: creditcard.ccNumber,
                Expiration: creditcard.ccExpiryMonth + creditcard.ccExpiryYear,
                VerifyCard: verifyCard, //false
            };
            const fiservResult = await setFiservCardToken(
                result.properties["providerUrl"],
                accessToken,
                ft,
                creditcard,
            );
            return fiservResult;
        } else {
            // Paymetict code goes here.
            const merchantGuid = result.properties["merchantGuid"];
            const dIeCommURL = result.properties["providerUrl"];
            const fdd: any = {
                MerchantGuid: merchantGuid,
                AccessToken: accessToken,
                "FormFields[0][Name]": "MyField",
                "FormFields[0][IsToTokenize]": "true",
                "FormFields[0][Value]": creditcard.ccNumber,
                addField: "function (field) {this.FormFields.push(field);}",
            };
            let formBody: any = [];
            for (let property in fdd) {
                let encodedKey = encodeURIComponent(property);
                let encodedValue = encodeURIComponent(fdd[property]);
                formBody.push(encodedKey + "=" + encodedValue);
            }
            formBody = formBody.join("&");

            setPaymetricCardToken(dIeCommURL, accessToken, formBody, creditcard);
        }
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        throw error;
    }
}

export async function setCreditCard(creditCard: ADICreditCardsModelNA): Promise<ADICreditCardsModelNA> {
    return await patch(`${accessTokenUrl}`, creditCard);
}

export async function getAccessToken(parameter: GetAccessTokenApiParameter): Promise<any> {
    const source = parameter.source ? "?source=" + parameter.source : "";
    try {
        const result = await get<any>(`${accessTokenUrl}/${parameter.accessToken}${source}`);
        return result;
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        throw error;
    }
}

export async function setFiservCardToken(
    url: string,
    accessToken: string,
    data: any,
    creditcard: ADICreditCardsModelNA,
) {
    try {
        const fiservToken = await post(url, data);
        const result = await tokenSuccessHandler(fiservToken, accessToken, creditcard);
        return result;
    } catch (error) {
        return { code: data.status, message: "Error Adding Card" };
    }
}

export async function setPaymetricCardToken(
    url: string,
    accessToken: string,
    data: any,
    creditcard: ADICreditCardsModelNA,
) {
    try {
        const paymetricToken = fetch(url, {
            method: "post",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: data,
        });
        const result = await tokenSuccessHandler(paymetricToken, accessToken, creditcard);
        return result;
    } catch (error) {
        return { code: data.status, message: "Error Adding Card" };
    }
}

function getCardMask(type: string, card: string): string {
    let mask: string = card.substring(0, 4) + (type == "AX" ? "*******" : "********") + card.substring(card.length - 4);
    return mask;
}

async function tokenSuccessHandler(result: any, accessToken: string, creditcard: ADICreditCardsModelNA) {
    if ((result.ErrorCode = "00")) {
        creditcard.ccNumber = getCardMask(creditcard.ccType, creditcard.ccNumber);
        try {
            const ccCardResult = await setCreditCard(creditcard);
            return ccCardResult;
        } catch (error) {
            throw error;
        }
    }
}

// Commented below code and written below according to the P21 Change
// export async function removeCreditCardNANew(parameter: any): Promise<any> {
//     try {
//         const deleteCard = await fetch(deleteUrl, {
//             method: "DELETE",
//             headers: {
//                 "Content-Type": "application/json",
//             },
//             body: JSON.stringify(parameter.data),
//         });
//         return deleteCard;
//     } catch (error) {
//         throw error;
//     }
// }

// The below written function is from P21 Change
export async function removeCreditCardNANew(parameter: any): Promise<any> {
    try {
        const savedCreditCards = await patch<ADICreditCardsModelNA>(`${deleteUrl}`, parameter.data);
        return {
            result: savedCreditCards,
            successful: true,
        };
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        throw error;
    }
}

export async function generateAuthorizationMail(request: any) {
    try{
        const response = await fetch(genrateMailUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(request)
        });
        return response;
    } catch(error){
        throw error;
    }
    
}

export async function validateCardToken(request: EPAValidationRequest) {
    try{
        let result: any;
        result = await fetch(validateUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(request)
        });
        return result;
    } catch(error){
        throw error;
    }
    
}


